import * as CookieConsent from 'vanilla-cookieconsent';

/**
 * The consent calls the marketing shell makes (DIT-402).
 *
 * They live here rather than in `src/shared/consent` because `src/marketing/**`
 * is vendored from done-it-design and must not import landing-specific paths.
 * The npm package is not such a path, so this talks to it directly — the same
 * arrangement `config/entrypoints.ts` and `shared/config/links.ts` use.
 *
 * Nothing here duplicates logic that already exists on the shared side. The
 * language sync used to, and was a second implementation of a call with its own
 * guards missing; it now runs from the route element in `app/router/routes.tsx`,
 * which is landing code. What is left is the reopen action and a read of whether
 * the visitor has answered yet — both things only the marketing shell needs.
 */

/** Opens the preferences dialog — the footer's "Cookie settings" link. */
export function openCookiePreferences(): void {
  try {
    CookieConsent.showPreferences();
  } catch {
    // Not initialised — during the prerender, or if init failed. Nothing to open.
  }
}

/**
 * Has the visitor answered the cookie banner yet?
 *
 * `false` while the banner is still up, and during the prerender, where the
 * library does not exist. Callers use this to stay out of the way: on a phone the
 * banner is full-width along the bottom whatever `position` says, so anything
 * else anchored down there is simply underneath it.
 */
export function consentDecided(): boolean {
  try {
    return CookieConsent.validConsent();
  } catch {
    return false;
  }
}

/**
 * Runs `cb` once the visitor answers. Returns an unsubscribe for effect cleanup.
 *
 * `cc:onConsent` also fires on later page loads for someone who answered
 * previously, which is what makes it usable as "the answer is in" rather than
 * only "the answer just happened".
 */
export function onConsentDecided(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = () => cb();
  window.addEventListener('cc:onConsent', handler);
  window.addEventListener('cc:onFirstConsent', handler);
  return () => {
    window.removeEventListener('cc:onConsent', handler);
    window.removeEventListener('cc:onFirstConsent', handler);
  };
}
